import { type LinkProps, type SxProps, createTheme, responsiveFontSizes } from "@mui/material";
import { csCZ } from '@mui/material/locale';
import { csCZ as pickerLocale } from "@mui/x-date-pickers/locales";
import {
  Link as RouterLink,
  type LinkProps as RouterLinkProps,
} from "react-router-dom";
import * as React from "react";

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (Material UI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});

export const primaryColor = "#7b8a27";
export const disabledInputTextColor = "rgba(0, 0, 0, 0.4)";

const generateVariants = (
  variantNames: string[],
  variant: SxProps
) =>
  variantNames.map((variantName) => ({
    props: { variant: variantName },
    style: variant,
  }));

let theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: primaryColor,
    },
    text: {
      primary: `#58595B`,
    },
  },
  typography: {
    fontFamily: 'Outfit, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif'
  },
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
      styleOverrides: {
        root: {
          fontWeight: "500",
          textDecoration: "none",
        },
      },
      variants: [
        {
          props: { variant: "filled" },
          style: {
            backgroundColor: "white",
            color: `${primaryColor} !important`,
            borderRadius: "5px",
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "500",
          borderRadius: "15px",
        },
      },
      variants: [
        {
          props: { variant: 'white-green' },
          style: {
            backgroundColor: "white",
            color: primaryColor,
            borderRadius: '38px',
            width: '12rem',
            height: '3rem',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1.2rem',
            ":hover": {
              backgroundColor: primaryColor,
              color: '#fff'
            }
          }
        }
      ]
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontWeight: "300",
          wordWrap: "break-word",
        },
      },
      //@ts-expect-error Adding custom variant
      variants: [
        ...generateVariants(["h1", "h2", "h3"], {
          fontWeight: "600",
        }),
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontWeight: "300",
        },
        input: {
          '&::placeholder': {
            color: '#000',
            opacity: 1,
            fontWeight: 400
          },
          '&:disabled': {
            color: disabledInputTextColor,
            WebkitTextFillColor: disabledInputTextColor,
            WebkitTextStroke: `0.2px ${disabledInputTextColor}`,
          },
        }
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: "300",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontWeight: 400,
          textTransform: 'none',
          color: '#000'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          marginLeft: '5px',
          marginRight: '5px',
          borderRadius: 15,
          ':hover': {
            backgroundColor: primaryColor + '80',
            color: '#fff',
          },
          "&.Mui-selected": {
            backgroundColor: primaryColor,
            color: '#fff',
            ':hover': {
              backgroundColor: primaryColor
            }
          },
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f5f538',
          color: '#fff'
        }
      }
    }
  },
}, csCZ, pickerLocale);

theme = responsiveFontSizes(theme);

export { theme };

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    filled: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    'white-green': true;
  }
}
